import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { FileText, Download, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const CoachAgreementsTab = ({ agreements, loading, onRefresh }) => {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (agreement) => {
    setDeleting(agreement.id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/admin/agreements/${agreement.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Agreement "${agreement.title}" deleted`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting agreement:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete agreement');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  const getStatusBadge = (agreement) => {
    if (agreement.is_archived) return 'bg-blue-100 text-blue-700';
    if (agreement.status === 'completed') return 'bg-green-100 text-green-700';
    return 'bg-amber-100 text-amber-700';
  };

  const getStatusText = (agreement) => {
    if (agreement.is_archived) return 'Archived';
    return agreement.status;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-forest" />
          Coaching Agreements ({agreements.length})
        </CardTitle>
        <CardDescription>All agreements associated with this coach</CardDescription>
      </CardHeader>
      <CardContent>
        {agreements.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No agreements found</p>
        ) : (
          <div className="space-y-3">
            {agreements.map(agreement => (
              <div 
                key={agreement.id}
                className="flex items-center justify-between p-4 border rounded-lg"
                data-testid={`agreement-${agreement.id}`}
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-forest" />
                  <div>
                    <p className="font-medium">{agreement.title}</p>
                    <p className="text-sm text-gray-500">
                      {getStatusText(agreement)}
                      {agreement.signed_date && ` • Signed: ${agreement.signed_date}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusBadge(agreement)}>
                    {getStatusText(agreement)}
                  </Badge>
                  {agreement.document_url && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(agreement.document_url, '_blank')}
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Delete Agreement"
                        disabled={deleting === agreement.id}
                        data-testid={`delete-agreement-${agreement.id}`}
                      >
                        {deleting === agreement.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Agreement?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete the agreement "<strong>{agreement.title}</strong>"?
                          <br /><br />
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(agreement)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CoachAgreementsTab;
